import {DaysApiResponse} from 'common/types';
import {useMoment} from 'common/useMoment';
import {CalendarDigit, Day, Locale} from 'react-modern-calendar-datepicker';
import {CALENDAR_DAYS} from 'common/constants';

const locale: Locale = {
  // months list by order
  months: [
    'Jänner',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  // week days by order
  weekDays: [
    {
      name: 'Montag',
      short: 'Mo',
    },
    {
      name: 'Dienstag',
      short: 'Di',
    },
    {
      name: 'Mittwoch',
      short: 'Mi',
    },
    {
      name: 'Donnerstag',
      short: 'Do',
    },
    {
      name: 'Freitag',
      short: 'Fr',
    },
    {
      name: 'Samstag',
      short: 'Sa',
      isWeekend: true,
    },
    {
      name: 'Sonntag', // used for accessibility
      short: 'So', // displayed at the top of days' rows
      isWeekend: true, // is it a formal weekend or not?
    },
  ],
  // just play around with this number between 0 and 6
  weekStartingIndex: 6,
  // return a { year: number, month: number, day: number } object
  getToday(gregorainTodayObject: Day) {
    return gregorainTodayObject;
  },
  // return a native JavaScript date here
  toNativeDate(date: Day) {
    return new Date(date.year, date.month - 1, date.day);
  },
  // return a number for date's month length
  getMonthLength(date: Day) {
    return new Date(date.year, date.month, 0).getDate();
  },
  // return a transformed digit to your locale
  transformDigit(digit: CalendarDigit) {
    return digit;
  },
  // texts in the date picker
  nextMonth: 'nächster Monat',
  previousMonth: 'vorheriger Monat',
  openMonthSelector: 'Monat auswählen',
  openYearSelector: 'Jahr auswählen',
  closeMonthSelector: 'schließen',
  closeYearSelector: 'schließen',
  defaultPlaceholder: 'auswählen ...',
  // for input range value
  from: 'von',
  to: 'bis',
  // used for input value when multi dates are selected
  digitSeparator: ',',
  // if your provide -2 for example, year will be 2 digited
  yearLetterSkip: 0,
  // is your language rtl or ltr?
  isRtl: false,
};

export const useCalendar = () => {
  const moment = useMoment();
  const start = moment.utc();
  const end = moment.utc().add(CALENDAR_DAYS, 'days');
  const minimumDate: Day = {
    day: start.get('date'),
    month: start.get('month') + 1,
    year: start.get('year'),
  };
  const maximumDate: Day = {
    day: end.get('date'),
    month: end.get('month') + 1,
    year: end.get('year'),
  };
  const endingYear = moment.utc().get('year') + 3;
  const startingYear = moment.utc().get('year') - 2;
  const getDisabledDays = (days?: DaysApiResponse['days']): Day[] =>
    days
      ?.filter(day => day.blocked)
      ?.map(({date}) => {
        const dayObject = moment.utc(date);
        return {
          day: dayObject.get('date'),
          month: dayObject.get('month') + 1,
          year: dayObject.get('year'),
        };
      }) || [];
  const dayToDate = (obj: Day, type: 'start' | 'end'): Date => {
    const dayObject = moment.utc();
    dayObject.set('date', obj.day);
    dayObject.set('month', obj.month - 1);
    dayObject.set('year', obj.year);
    if (type === 'start') return dayObject.startOf('day').toDate();
    return dayObject.endOf('day').toDate();
  };
  const dateToDay = (date: Date): Day => {
    const dayObject = moment.utc(date);
    return {
      day: dayObject.get('date'),
      month: dayObject.get('month') + 1,
      year: dayObject.get('year'),
    };
  };
  return {
    minimumDate,
    maximumDate,
    endingYear,
    startingYear,
    getDisabledDays,
    locale,
    dayToDate,
    dateToDay,
  };
};
